import React, { useState } from 'react';

/**
 * Backend API Configuration
 * 
 * The contact form sends data to the backend API which uses nodemailer
 * to send emails securely from the server.
 * 
 * Backend server should be running on:
 * - Development: http://localhost:3001
 * - Production: Update REACT_APP_API_URL in .env
 */
const API_URL = process.env.REACT_APP_API_URL;

const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage('');

    try {
      // Send data to backend API
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          description: formData.description.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      // Success
      console.log('Email sent successfully:', data);
      setSubmitStatus('success');
      setStatusMessage('Thank you! We have received your message and will contact you soon.');
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', description: '' });
      
      // Close modal after 3 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
        setStatusMessage('');
      }, 3000);
    } catch (error) {
      // Error handling
      console.error('Error sending message:', error);

      let errorMessage = 'Sorry, there was an error sending your message. ';
      
      if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
        errorMessage += 'Please check your internet connection and make sure the server is running.';
      } else if (error.message.includes('required')) {
        errorMessage += 'Please fill in all required fields.';
      } else if (error.message.includes('Invalid email')) {
        errorMessage += 'Please enter a valid email address.';
      } else {
        errorMessage += error.message || 'Please try again later or contact us directly.';
      }

      setSubmitStatus('error');
      setStatusMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 md:p-8 animate-fade-in border border-black/10">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-ink-muted hover:text-ink transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Form Header */}
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-extrabold text-ink mb-2">
            Contact Sales
          </h2>
          <p className="text-ink-muted">
            Fill out the form below and we'll get back to you soon.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-ink mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border border-black/15 bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition"
              placeholder="Enter your name"
            />
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border border-black/15 bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition"
              placeholder="Enter your email"
            />
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-ink mb-2">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border border-black/15 bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition"
              placeholder="Enter your phone number"
            />
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-ink mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-black/15 bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition resize-none"
              placeholder="Tell us about your requirements..."
            ></textarea>
          </div>

          {/* Status Message */}
          {submitStatus && (
            <div className={`p-4 rounded-lg ${
              submitStatus === 'success' 
                ? 'bg-green-100 border border-green-300' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`text-sm ${
                submitStatus === 'success' 
                  ? 'text-green-800' 
                  : 'text-red-700'
              }`}>
                {statusMessage}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-brand hover:bg-brand-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;

